import { forwardRef, useCallback, useMemo, useState } from "react";
import { tokens } from "@opendoor/tokens-studio/dist/bricks-next/tokens/Components";
import { isWeb, styled, Text as TamaguiText, View } from "@tamagui/core";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Close, Download, Edit, FavoritedFilled, FavoriteUnfilled, Filter, Menu, NearMe, PasswordHide, Share } from "../../__generated__/icons/index.mjs";
import { getStateTokenValue } from "../../helpers/tokens.mjs";
import { createAnchorOrButtonFrame } from "../shared/anchorOrButton.mjs";
import { jsx } from "react/jsx-runtime";
const {
    iconButton: iconButtonTokens
  } = tokens,
  sharedFrameConfig = {
    name: "IconButton",
    userSelect: "none",
    cursor: "pointer",
    borderStyle: "solid",
    borderWidth: iconButtonTokens.borderWidth.default,
    borderRadius: iconButtonTokens.cornerRadius.default,
    alignItems: "center",
    justifyContent: "center",
    // Mobile safari sometimes adds a default horizontal padding to buttons
    padding: 0,
    animation: "fastEaseOut",
    animateOnly: isWeb ? ["transform", "background-color", "border-color", "border-top-color", "border-left-color", "border-bottom-color", "border-right-color"] : ["transform", "backgroundColor", "borderColor", "borderTopColor", "borderLeftColor", "borderBottomColor", "borderRightColor"],
    focusVisibleStyle: {
      borderColor: iconButtonTokens.color.icon.border.state.focused.default
    },
    pressStyle: {
      scale: getStateTokenValue(iconButtonTokens.state.pressedScale)
    },
    variants: {
      variant: (variant, {
        props
      }) => props.disabled ? {
        backgroundColor: iconButtonTokens.color.icon.background.state.inactive[variant],
        borderColor: iconButtonTokens.color.icon.border.state.inactive[variant]
      } : {
        backgroundColor: iconButtonTokens.color.icon.background[variant],
        borderColor: iconButtonTokens.color.icon.border[variant],
        hoverStyle: {
          backgroundColor: iconButtonTokens.color.icon.background.state.hover[variant],
          borderColor: iconButtonTokens.color.icon.border.state.hover[variant]
        }
      },
      frameSize: size => ({
        width: iconButtonTokens.size[size],
        height: iconButtonTokens.size[size],
        ...(size === "small" ? {
          backgroundColor: "$transparent",
          borderColor: "$transparent",
          hoverStyle: {
            borderColor: "$transparent"
          }
        } : {})
      }),
      disabled: {
        true: {
          pointerEvents: "none"
        }
      }
    }
  },
  AnchorFrame = styled(TamaguiText, {
    ...sharedFrameConfig,
    tag: "a",
    accessibilityRole: "link",
    textDecorationLine: "none",
    display: "flex",
    position: "relative"
  }),
  ButtonFrame = styled(View, {
    ...sharedFrameConfig,
    tag: "button",
    accessibilityRole: "button"
  }),
  AnchorOrButtonFrame = createAnchorOrButtonFrame({
    AnchorFrame,
    ButtonFrame
  }),
  IconButton = forwardRef(function ({
    variant = "default",
    size,
    disabled,
    icon,
    ...rest
  }, ref) {
    const iconColor = disabled ? iconButtonTokens.color.icon.content.state.inactive.default : icon.color ?? iconButtonTokens.color.icon.content[variant],
      iconSize = icon.size ?? (size === "large" ? 24 : 20);
    return /* @__PURE__ */jsx(AnchorOrButtonFrame, {
      variant,
      frameSize: size,
      disabled,
      "aria-disabled": disabled,
      focusable: !disabled,
      ref,
      ...rest,
      children: /* @__PURE__ */jsx(icon.Component, {
        "aria-label": icon.label,
        color: iconColor,
        size: iconSize
      })
    });
  }),
  CloseButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: Close,
      label: iconLabel ?? "close",
      size: iconSize
    },
    ...props
  }),
  MenuButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: Menu,
      label: iconLabel ?? "menu",
      size: iconSize
    },
    ...props
  }),
  ChevronBackButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: ChevronLeft,
      label: iconLabel ?? "back",
      size: iconSize
    },
    ...props
  }),
  ChevronForwardButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: ChevronRight,
      label: iconLabel ?? "forward",
      size: iconSize
    },
    ...props
  }),
  ChevronUpButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: ChevronUp,
      label: iconLabel ?? "up",
      size: iconSize
    },
    ...props
  }),
  ChevronDownButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: ChevronDown,
      label: iconLabel ?? "down",
      size: iconSize
    },
    ...props
  }),
  EditButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: Edit,
      label: iconLabel ?? "edit",
      size: iconSize
    },
    ...props
  }),
  DownloadButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: Download,
      label: iconLabel ?? "download",
      // Medium size uses 24px icon
      size: iconSize ?? (props.size === "small" ? 20 : 24)
    },
    ...props
  }),
  FilterButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: Filter,
      label: iconLabel ?? "filter",
      size: iconSize
    },
    ...props
  }),
  FavoriteButton = ({
    iconLabel,
    iconSize,
    defaultFavorited,
    isFavorited,
    onPress,
    ...props
  }) => {
    const [internalIsFavorited, setInternalIsFavorited] = useState(defaultFavorited ?? !1),
      isControlledComponent = typeof isFavorited < "u",
      favorited = isControlledComponent ? isFavorited : internalIsFavorited,
      handlePress = useCallback(e => (!isControlledComponent && setInternalIsFavorited(prev => !prev), onPress?.(e)), [isControlledComponent, onPress]),
      icon = useMemo(() => favorited ? {
        Component: FavoritedFilled,
        label: iconLabel ?? "unfavorite",
        size: iconSize,
        color: iconButtonTokens.color.icon.content.favorite
      } : {
        Component: FavoriteUnfilled,
        label: iconLabel ?? "favorite",
        size: iconSize
      }, [favorited, iconLabel, iconSize]);
    return /* @__PURE__ */jsx(IconButton, {
      icon,
      value: favorited ? "false" : "true",
      onPress: handlePress,
      ...props
    });
  },
  NearMeButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: NearMe,
      label: iconLabel ?? "near me",
      size: iconSize
    },
    ...props
  }),
  HideButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: PasswordHide,
      label: iconLabel ?? "hide",
      size: iconSize
    },
    ...props
  }),
  ShareButton = ({
    iconLabel,
    iconSize,
    ...props
  }) => /* @__PURE__ */jsx(IconButton, {
    icon: {
      Component: Share,
      label: iconLabel ?? "share",
      // Medium size uses 24px icon
      size: iconSize ?? (props.size === "small" ? 20 : 24)
    },
    ...props
  });
export { ChevronBackButton, ChevronDownButton, ChevronForwardButton, ChevronUpButton, CloseButton, DownloadButton, EditButton, FavoriteButton, FilterButton, HideButton, IconButton, MenuButton, NearMeButton, ShareButton };